import { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import { useOrgTimezone, formatLocalDateInTz, formatDateTimeInTz } from '../../utils/timezone';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Droplets, Edit, UserCheck, Play, CheckCircle, XCircle,
  MapPin, Calendar, User, Package, AlertTriangle, FlaskConical, Gauge,
  ClipboardCheck, Printer, Sprout,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import {
  SprayStatusBadge,
  WeatherPanel,
  SprayCoverageMap,
  SprayWaypointTracker,
  TankLogPanel,
  ReconciliationPanel,
  MixCompatibilityPanel,
  CalibrationWizard,
  CalibrationStatusCard,
  PreStartStatusCard,
  WeatherLimitsCard,
  WeatherLimitAckDialog,
} from '../../components/spray';
import RunChecklistModal from '../../components/equipment/RunChecklistModal';
import sprayService from '../../services/spray';
import equipmentService from '../../services/equipment';
import TankDoseTable, { type TankDoseProduct } from '../../components/spray/TankDoseTable';
import waypointQueue from '../../services/waypointQueue';
import { uploadBeforeFinish, describeUploadFailure, stopTrackingFor } from '../../services/finishTracking';
import { requestLocationWithDisclosure } from '../../services/locationGate';
import { useAuth } from '../../context/AuthContext';
import type {
  SprayApplication, SignOffData, CoverageResult, SprayReconciliation,
  PreStartMachineStatus, WeatherLimitCheck,
} from '../../types/spray';
import type { CalibratableEquipment } from '../../components/spray/CalibrationWizard';
import CredentialOverrideDialog from '../../components/spray/CredentialOverrideDialog';
import WhpOverridePanel from '../../components/spray/WhpOverridePanel';
import SprayTargetLotsCard from '../../components/spray/SprayTargetLotsCard';
import SprayQuarantineCard from '../../components/spray/SprayQuarantineCard';
import { APPLICATION_METHOD_LABELS } from '../../types/spray';
import type { CredentialGaps } from '../../services/sprayCredential';
import type { ApiError } from '../../types';
import { toNum, toNumOr } from '@/utils/numeric';
import { downloadBlob } from '@/utils/downloadBlob';

// ============================================================================
// Status timeline
// ============================================================================

const STATUS_STEPS = ['created', 'assigned', 'in_progress', 'completed'] as const;

function StatusTimeline({ status }: { status: string }) {
  const isCancelled = status === 'cancelled';
  const currentIdx = STATUS_STEPS.indexOf(status as any);

  if (isCancelled) {
    return (
      <div className="flex items-center gap-2 text-red-600 text-sm font-medium">
        <XCircle className="h-5 w-5" />
        Cancelled
      </div>
    );
  }

  // Steps are a fixed 3.5rem so the labels have somewhere to wrap; the
  // connectors take whatever is left. Nothing here may be wider than its
  // container, or the last label falls off the right of a phone screen.
  return (
    <div className="flex w-full items-start">
      {STATUS_STEPS.map((s, i) => {
        const done = i <= currentIdx;
        const active = i === currentIdx;
        const labels: Record<string, string> = {
          created: 'Created', assigned: 'Assigned', in_progress: 'In Progress', completed: 'Completed',
        };
        return (
          <Fragment key={s}>
            <div className="flex w-14 shrink-0 flex-col items-center text-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors ${
                done
                  ? active
                    ? 'bg-primary-600 border-primary-600 text-white'
                    : 'bg-primary-100 border-primary-400 text-primary-700'
                  : 'bg-white border-neutral-300 text-neutral-400'
              }`}>
                {done && !active ? (
                  <CheckCircle className="h-4 w-4" />
                ) : (
                  <span className="text-xs font-bold">{i + 1}</span>
                )}
              </div>
              <span className={`text-xs mt-1 leading-tight ${
                active ? 'text-primary-600 font-semibold' : done ? 'text-primary-500' : 'text-neutral-400'
              }`}>
                {labels[s]}
              </span>
            </div>
            {i < STATUS_STEPS.length - 1 && (
              <div className={`mt-4 h-0.5 min-w-2 flex-1 ${i < currentIdx ? 'bg-primary-400' : 'bg-neutral-200'}`} />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

// ============================================================================
// Assign operator modal
// ============================================================================

function AssignOperatorModal({
  sprayId,
  onClose,
  onAssigned,
}: {
  sprayId: number;
  onClose: () => void;
  onAssigned: () => void;
}) {
  const [users, setUsers] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<number | ''>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gaps, setGaps] = useState<CredentialGaps | null>(null);

  useEffect(() => {
    sprayService.getSprayLicensedUsers().then(res => {
      if (res.success && res.data) {
        // Users holding spray.operator, spray.creator, or spray.admin — creators
        // and admins can operate sprays in addition to their own workflows.
        const operators = res.data.filter(u =>
          (u.spray_licenses || []).some(l => ['operator', 'creator', 'admin'].includes(l))
        );
        setUsers(operators);
      }
    }).catch(() => { /* the picker stays empty */ });
  }, []);

  const selectedUser = users.find(u => u.user_id === Number(selectedId));
  const selectedName = selectedUser?.name || selectedUser?.email || `User ${selectedId}`;

  const doAssign = async (overrideReason?: string) => {
    if (!selectedId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await sprayService.assignOperator(sprayId, Number(selectedId), overrideReason);
      if (res.success) {
        setGaps(null);
        onAssigned();
      } else {
        setError((res as any).error?.message || 'Failed to assign operator');
      }
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr?.status === 422 && apiErr.code === 'CREDENTIAL_GAPS' && apiErr.details?.gaps) {
        // Show override dialog; user will confirm with a reason.
        setGaps(apiErr.details.gaps as CredentialGaps);
      } else {
        setError(apiErr?.message || 'Failed to assign operator');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = () => doAssign();
  const handleOverrideConfirm = (reason: string) => doAssign(reason);

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
          <h3 className="text-base font-semibold text-neutral-800 mb-4">Assign Operator</h3>
          <select
            value={selectedId}
            onChange={e => setSelectedId(e.target.value ? Number(e.target.value) : '')}
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Select operator...</option>
            {users.map(u => (
              <option key={u.user_id} value={u.user_id}>
                {u.name || u.email}
              </option>
            ))}
          </select>
          {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={handleAssign} disabled={!selectedId || loading}>
              {loading ? 'Assigning...' : 'Assign'}
            </Button>
          </div>
        </div>
      </div>
      {gaps && (
        <CredentialOverrideDialog
          operatorName={selectedName}
          gaps={gaps}
          submitting={loading}
          onCancel={() => setGaps(null)}
          onConfirm={handleOverrideConfirm}
        />
      )}
    </>
  );
}

// ============================================================================
// Sign-off modal
// ============================================================================

function SignOffModal({
  spray,
  onClose,
  onSigned,
}: {
  spray: SprayApplication;
  onClose: () => void;
  onSigned: () => void;
}) {
  const [postNotes, setPostNotes]           = useState('');
  const [chemUsed, setChemUsed]             = useState<number | ''>(
    toNum(spray.total_chemical_used) ?? ''
  );
  const [volMixed, setVolMixed]             = useState<number | ''>(
    toNum(spray.total_volume_mixed) ?? ''
  );
  const [preview, setPreview]               = useState<SprayReconciliation | null>(null);

  // What the tank records add up to, before anything is committed. Showing the
  // applied rate and the label verdict at this point is the whole reason the
  // tank log exists — after sign-off the verdict is frozen.
  useEffect(() => {
    sprayService.getReconciliation(spray.id)
      .then(res => { if (res.success && res.data) setPreview(res.data); })
      .catch(() => {});
  }, [spray.id]);

  // With a tank log, the two free-typed totals are redundant and worse than
  // redundant — a number typed from memory silently outranks records made at
  // the time. They are only offered for a spray with no tanks recorded.
  const hasTankLog = (preview?.tanks_used ?? 0) > 0;

  // The product's own unit. The field used to be hardcoded to litres, which
  // quietly booked millilitres of a mL/100L product as litres of stock.
  const productUnit = (spray.rate_unit || 'L/ha').split('/')[0];
  const [locationLat, setLocationLat]       = useState<number | null>(null);
  const [locationLng, setLocationLng]       = useState<number | null>(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [submitting, setSubmitting]         = useState(false);
  const [error, setError]                   = useState<string | null>(null);

  const getLocation = async () => {
    setGettingLocation(true);
    const loc = await requestLocationWithDisclosure();
    if (loc) {
      setLocationLat(loc.coords.latitude);
      setLocationLng(loc.coords.longitude);
    }
    setGettingLocation(false);
  };

  // GPS points still on the phone. They have to reach the server before the
  // spray closes — after that it refuses them — so sign-off uploads them
  // first, and only finishes without them when the operator says so.
  const [pointsLeft, setPointsLeft]         = useState<number | null>(null);
  const [strandedPoints, setStrandedPoints] = useState(0);

  const handleSubmit = async (discardPoints = false) => {
    setSubmitting(true);
    setError(null);
    const entity = { type: 'spray' as const, id: spray.id };
    let restore: () => Promise<void> = async () => {};
    try {
      if (!discardPoints) {
        const upload = await uploadBeforeFinish(entity, setPointsLeft);
        setPointsLeft(null);
        restore = upload.restore;
        if (upload.remaining > 0) {
          await restore();
          setStrandedPoints(upload.remaining);
          setError(describeUploadFailure(upload));
          return;
        }
      }
      setStrandedPoints(0);
      const data: SignOffData = {
        post_spray_notes: postNotes || undefined,
        total_chemical_used: chemUsed !== '' ? Number(chemUsed) : null,
        total_volume_mixed: volMixed !== '' ? Number(volMixed) : null,
        sign_off_location_lat: locationLat,
        sign_off_location_lng: locationLng,
      };
      const res = await sprayService.completeApplication(spray.id, data).catch((e: Error) => {
        setError(e.message || 'Failed to complete spray');
        return null;
      });
      if (res?.success) {
        await stopTrackingFor(entity);
        onSigned();
      } else {
        await restore();
        if (res) setError((res as any).error?.message || 'Failed to complete spray');
      }
    } finally {
      setPointsLeft(null);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-base font-semibold text-neutral-800 flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-600" />
          Sign Off Spray
        </h3>

        {hasTankLog ? (
          <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2">
            <p className="text-xs text-neutral-600">
              Totals come from your {preview?.tanks_used} recorded{' '}
              {preview?.tanks_used === 1 ? 'tank' : 'tanks'} —{' '}
              {preview?.total_water_l.toFixed(0)} L of water. Nothing to retype.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">
                Chemical used ({productUnit})
              </label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={chemUsed}
                onChange={e => setChemUsed(e.target.value === '' ? '' : parseFloat(e.target.value))}
                className="w-full border border-neutral-300 rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Volume mixed (L)</label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={volMixed}
                onChange={e => setVolMixed(e.target.value === '' ? '' : parseFloat(e.target.value))}
                className="w-full border border-neutral-300 rounded px-3 py-2 text-sm"
              />
            </div>
          </div>
        )}

        {preview && preview.products.length > 0 && (
          <ReconciliationPanel reconciliation={preview} preview />
        )}

        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">Post-spray notes</label>
          <textarea
            value={postNotes}
            onChange={e => setPostNotes(e.target.value)}
            rows={3}
            className="w-full border border-neutral-300 rounded px-3 py-2 text-sm resize-none"
            placeholder="Any observations or issues..."
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-medium text-neutral-600">Sign-off location</label>
            <button
              type="button"
              onClick={getLocation}
              disabled={gettingLocation}
              className="text-xs text-primary-600 hover:underline"
            >
              {gettingLocation ? 'Getting location...' : 'Use my location'}
            </button>
          </div>
          {locationLat && locationLng && (
            <p className="text-xs text-neutral-500">
              {locationLat.toFixed(5)}, {locationLng.toFixed(5)}
            </p>
          )}
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        {pointsLeft != null && pointsLeft > 0 && (
          <p className="text-xs text-neutral-500">
            Uploading GPS track — {pointsLeft} point{pointsLeft === 1 ? '' : 's'} to go…
          </p>
        )}

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          {strandedPoints > 0 && !submitting && (
            <Button variant="outline" size="sm" onClick={() => handleSubmit(true)}>
              Complete without them
            </Button>
          )}
          <Button variant="primary" size="sm" onClick={() => handleSubmit()} disabled={submitting}>
            {submitting
              ? (pointsLeft ? 'Uploading GPS…' : 'Submitting...')
              : strandedPoints > 0 ? 'Try again' : 'Complete Spray'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Cancel modal
// ============================================================================

function CancelModal({
  sprayId,
  onClose,
  onCancelled,
}: {
  sprayId: number;
  onClose: () => void;
  onCancelled: () => void;
}) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCancel = async () => {
    setLoading(true);
    const res = await sprayService.cancelApplication(sprayId, reason);
    if (res.success) onCancelled();
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
        <h3 className="text-base font-semibold text-neutral-800 mb-3 flex items-center gap-2">
          <XCircle className="h-5 w-5 text-red-500" />
          Cancel Spray
        </h3>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          rows={3}
          placeholder="Reason for cancellation (optional)"
          className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm mb-4 resize-none"
        />
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Keep</Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleCancel}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700"
          >
            {loading ? 'Cancelling...' : 'Cancel Spray'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main Detail page
// ============================================================================

function SprayDetail() {
  const timezone = useOrgTimezone();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [spray, setSpray]             = useState<SprayApplication | null>(null);
  const [coverage, setCoverage]       = useState<CoverageResult | null>(null);
  const [loading, setLoading]         = useState(true);
  const [loadFailed, setLoadFailed]   = useState(false);
  const [showAssign, setShowAssign]   = useState(false);
  const [showSignOff, setShowSignOff] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [showCalibration, setShowCalibration] = useState(false);
  const [calibrationEquipment, setCalibrationEquipment] = useState<CalibratableEquipment | null>(null);
  /** The machine whose pre-start checklist is open, if any. */
  const [preStartMachine, setPreStartMachine] = useState<PreStartMachineStatus | null>(null);
  const [showCancel, setShowCancel]   = useState(false);
  const [startingSpray, setStartingSpray] = useState(false);
  const [jobSheetBusy, setJobSheetBusy] = useState(false);
  const [jobSheetError, setJobSheetError] = useState<string | null>(null);
  /** The server's weather verdict when it refused Start — shown for the operator to accept or not. */
  const [weatherBreach, setWeatherBreach] = useState<WeatherLimitCheck | null>(null);
  /** Bumped after a start so the weather-limits card re-reads its breach history. */
  const [weatherLimitsKey, setWeatherLimitsKey] = useState(0);
  const [liveWeather, setLiveWeather] = useState<{ source: string; snapshot: Record<string, unknown> } | null>(null);
  const liveWeatherTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  /** The sprayer's tank size and water rate, for the per-tank and whole-job amounts. */
  const [tankSetup, setTankSetup] = useState<{ capacityL: number | null; rateLpha: number | null; plannedAreaHa: number | null } | null>(null);

  // Auto-open sign-off if navigated with #complete hash
  useEffect(() => {
    if (location.hash === '#complete' && spray?.status === 'in_progress') {
      setShowSignOff(true);
    }
  }, [location.hash, spray?.status]);

  const loadSpray = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await sprayService.getApplication(Number(id));
      setLoadFailed(false);
      if (res.success && res.data) {
        setSpray(res.data);
        // Load coverage if completed
        if (res.data.status === 'completed' || res.data.status === 'in_progress') {
          sprayService.getCoverage(Number(id)).then(covRes => {
            if (covRes.success && covRes.data) setCoverage(covRes.data);
          }).catch(() => { /* the map just stays empty */ });
        }
      }
    } catch {
      // This reruns on every return to the tab, often on paddock signal. A
      // failed refetch keeps the spray already on screen; only a page that
      // never loaded says so. Without the catch a timeout here was an
      // unhandled rejection (ApiError "Request timed out") on every refocus.
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadSpray(); }, [loadSpray]);

  // Tank size and water rate for "What goes in each tank". The make-up
  // estimate carries both from the server; the sprayer's own record is the
  // fallback for a backend that does not answer it before the spray starts.
  // Both are best-effort: without them the job totals still show.
  const sprayIdForTank = spray?.id;
  const sprayEquipmentId = spray?.equipment_id;
  useEffect(() => {
    if (!sprayIdForTank) return;
    let cancelled = false;
    (async () => {
      let capacityL: number | null = null;
      let rateLpha: number | null = null;
      let plannedAreaHa: number | null = null;
      try {
        const m = await sprayService.getTankMakeUp(sprayIdForTank);
        if (m.success && m.data?.available) {
          capacityL = m.data.capacity_l;
          // The calibrated rate is the plan; mid-job the measured one replaces it.
          rateLpha = m.data.calibrated_rate_lpha ?? m.data.rate_lpha;
          plannedAreaHa = m.data.planned_area_ha;
        }
      } catch { /* fall through to the sprayer's record */ }
      if (capacityL == null && sprayEquipmentId) {
        try {
          const e = await equipmentService.getById(sprayEquipmentId);
          if (e.success && e.data) {
            capacityL = toNum(e.data.tank_capacity_litres);
            rateLpha = rateLpha ?? toNum(e.data.output_rate_lpha);
          }
        } catch { /* no access to the machine — the totals still show */ }
      }
      if (!cancelled) setTankSetup({ capacityL, rateLpha, plannedAreaHa });
    })();
    return () => { cancelled = true; };
  }, [sprayIdForTank, sprayEquipmentId]);

  // Fetch live current weather for active sprays; poll every 5 min when in_progress
  useEffect(() => {
    if (!id || !spray) return;
    const activeStatuses = ['created', 'assigned', 'in_progress', 'paused'];
    if (!activeStatuses.includes(spray.status)) return;

    const fetchLive = () => {
      sprayService.getCurrentWeather(Number(id)).then(res => {
        if (res.success) setLiveWeather(res.data ?? null);
      }).catch(() => { /* non-blocking */ });
    };

    fetchLive();

    if (spray.status === 'in_progress') {
      liveWeatherTimerRef.current = setInterval(fetchLive, 5 * 60 * 1000);
    }
    return () => {
      if (liveWeatherTimerRef.current) clearInterval(liveWeatherTimerRef.current);
    };
  }, [id, spray?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch on a 409 for this spray, or on tab refocus — the contextual
  // action button keys off cached status, and a 409 is the unambiguous
  // "your view is stale" signal.
  useEffect(() => {
    if (!id) return;
    const sprayIdNum = Number(id);
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void loadSpray();
    };
    document.addEventListener('visibilitychange', onVisible);
    const unsub = waypointQueue.subscribeStatus((s) => {
      const e = s.lastError;
      if (e?.status === 409 && e.entityType === 'spray' && e.entityId === sprayIdNum) {
        void loadSpray();
      }
    });
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      unsub();
    };
  }, [id, loadSpray]);

  const handleStart = async (acknowledgeWeather = false) => {
    if (!spray) return;
    setStartingSpray(true);
    setStartError(null);
    try {
      const res = await sprayService.startApplication(spray.id, acknowledgeWeather);
      if (res.success) {
        setSpray(res.data!);
        setWeatherBreach(null);
        setWeatherLimitsKey(k => k + 1);
      } else {
        // A refusal used to vanish — the button simply re-enabled and the
        // operator was left to guess. The server has real reasons to say no
        // (withholding-period approval pending, calibration outstanding), and
        // each of them is actionable, so each has to be shown.
        setStartError(
          (res as { error?: { message?: string } }).error?.message
          ?? 'Could not start this spray'
        );
        loadSpray();
      }
    } catch (err) {
      const apiErr = err as ApiError;
      const check = apiErr?.details?.weather_check as WeatherLimitCheck | undefined;
      if (apiErr?.status === 409 && apiErr.code === 'WEATHER_LIMITS_BREACHED' && check) {
        // Not a refusal to report — a question for the operator: here is
        // what is outside the limits, start anyway?
        setWeatherBreach(check);
      } else {
        setStartError(apiErr?.message || 'Could not start this spray');
        // The block may have been cleared elsewhere since this page loaded.
        loadSpray();
      }
    } finally {
      setStartingSpray(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="animate-pulse space-y-4">
          <div className="h-12 bg-neutral-100 rounded-lg" />
          <div className="h-32 bg-neutral-100 rounded-lg" />
          <div className="h-48 bg-neutral-100 rounded-lg" />
        </div>
      </div>
    );
  }

  if (!spray) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 text-center py-16">
        <Droplets className="h-12 w-12 mx-auto mb-3 text-neutral-300" />
        <p className="text-neutral-500">
          {loadFailed ? 'Could not load this spray. Check your connection and try again.' : 'Spray application not found'}
        </p>
        {loadFailed && (
          <Button className="mt-4 mr-2" onClick={() => void loadSpray()}>
            Try again
          </Button>
        )}
        <Button variant="outline" className="mt-4" onClick={() => navigate('/spray')}>
          Back to Dashboard
        </Button>
      </div>
    );
  }

  const isOperator = user?.id === spray.assigned_operator_id;
  const isCreatorOrAdmin = spray.created_by === user?.id || (user as any)?.isAdmin;
  const operatorSpraying = spray.status === 'in_progress' && isOperator;

  const openCalibration = () => {
    // The wizard reads only these off a machine here; it used to be cast to a
    // whole equipment record it never had. The geometry matters: without the
    // boom or strip width it asked a boom or a weed-strip sprayer for a row
    // spacing and derived the rate against the row.
    setCalibrationEquipment({
      id: spray.equipment_id as number,
      name: spray.equipment_name ?? 'Sprayer',
      equipment_type: spray.equipment_type ?? 'airblast_sprayer',
      boom_width_m: spray.equipment_boom_width_m,
      nozzle_spacing_cm: spray.equipment_nozzle_spacing_cm,
      strip_width_m: spray.equipment_strip_width_m,
      operating_speed_kph: spray.equipment_operating_speed_kph,
    });
    setShowCalibration(true);
  };

  // The calibration gate. Outstanding means the creator demanded one and it
  // has not been done — the server refuses to start the spray in that state,
  // so the button has to refuse too rather than firing a request that 409s.
  const calibrationOutstanding = spray.calibration_required === true && !spray.calibration_id;

  // The pre-start gate, same contract: every machine on the rig — the sprayer
  // AND the tractor pulling it — needs a walk-around today before the server
  // will start the spray. `satisfied` is the server's own verdict rather than
  // a rule re-derived here, so the button and the refusal cannot disagree.
  const preStartMachines = spray.pre_start_status ?? [];
  const preStartOutstanding = preStartMachines.filter(m => !m.satisfied);

  // Calibration first when both are outstanding: it is the one a creator
  // deliberately demanded for this job, and sending the operator to two
  // screens at once helps nobody.
  const blockedBy: 'calibration' | 'pre-start' | null =
    calibrationOutstanding ? 'calibration'
    : preStartOutstanding.length ? 'pre-start'
    : null;

  // Row spacing to offer as the airblast swath default. Taken from the spray's
  // own targets so the operator is not asked for a number the system knows.
  const suggestedRowSpacing =
    spray.reconciliation?.output_rate_per_target?.find(t => t.row_spacing_m != null)?.row_spacing_m
    ?? null;

  // Every product in the mix; a single-product spray is a mix of one.
  const isMix = (spray.chemicals?.length ?? 0) > 1;
  const mixChemicalIds = (spray.chemicals ?? []).map(c => c.chemical_id);

  // Prefer the mix-wide maxima the server computes. Falling back to the
  // primary's own values keeps sprays created before tank mixes rendering.
  const effectiveWhpDays  = spray.mix_whp_days  ?? spray.whp_days  ?? null;
  const effectiveReiHours = spray.mix_rei_hours ?? spray.rei_hours ?? null;

  // Every product at the dose that goes in the tank — rate × concentration,
  // the same figure the mix card shows beside each product.
  const tankDoseProducts: TankDoseProduct[] = (spray.chemicals?.length
    ? [...spray.chemicals].sort((a, b) => Number(!!b.is_primary) - Number(!!a.is_primary)).map(c => {
        const rate = toNum(c.rate ?? (c.is_primary ? spray.rate_per_ha : null));
        const factor = toNumOr(c.concentration_factor ?? (c.is_primary ? spray.concentration_factor : null), 1);
        return {
          key: c.id,
          name: c.product_name || (c.is_primary ? spray.chemical_name : '') || 'Product',
          rate: rate == null ? null : rate * factor,
          rateUnit: c.rate_unit ?? (c.is_primary ? spray.rate_unit : null) ?? 'L/ha',
        };
      })
    : spray.chemical_name
      ? [{
          key: 'primary',
          name: spray.chemical_name,
          rate: toNum(spray.rate_per_ha) == null ? null : toNumOr(spray.rate_per_ha) * toNumOr(spray.concentration_factor, 1),
          rateUnit: spray.rate_unit || 'L/ha',
        }]
      : []);
  const tankRateLpha = tankSetup?.rateLpha
    ?? toNum(spray.applied_calibration?.output_rate_lpha)
    ?? toNum(spray.latest_calibration?.output_rate_lpha);
  const jobAreaHa = spray.is_nursery
    ? null
    : toNum(spray.effective_area_ha) ?? tankSetup?.plannedAreaHa ?? null;
  // A nursery spray goes on to run-off: its water is plants × run-off, not area × rate.
  const jobWaterL = spray.is_nursery
    ? (spray.target_plant_count && toNum(spray.runoff_l_per_plant)
        ? spray.target_plant_count * toNumOr(spray.runoff_l_per_plant)
        : null)
    : jobAreaHa != null && tankRateLpha ? jobAreaHa * tankRateLpha : null;

  const harvestDate = spray.whp_days && spray.scheduled_date
    ? sprayService.earliestHarvestDate(spray.scheduled_date, spray.whp_days)
    : null;

  // A PDF rather than a print page: the app's web view cannot print, but a
  // downloaded PDF opens in whatever the phone has.
  const downloadJobSheet = async () => {
    setJobSheetBusy(true);
    setJobSheetError(null);
    try {
      downloadBlob(await sprayService.getJobSheetPdf(spray.id), `job-sheet-${spray.reference_number || spray.id}.pdf`);
    } catch {
      setJobSheetError('Could not make the job sheet. Try again.');
    } finally {
      setJobSheetBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title={spray.title}
        subtitle={spray.reference_number ? `${spray.reference_number} · ${spray.chemical_name || ''}` : spray.chemical_name || ''}
        icon={<Droplets className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex items-center gap-2 flex-wrap">
            <SprayStatusBadge status={spray.status} />

            {/* Job sheet — for the job ahead, or a copy of what was issued for a finished one */}
            {spray.status !== 'cancelled' && (
              <Button variant="outline" size="sm" onClick={downloadJobSheet} disabled={jobSheetBusy}>
                <Printer className="h-4 w-4" />
                {jobSheetBusy ? 'Preparing…' : 'Job sheet'}
              </Button>
            )}

            {/* Edit — creator/admin on created or assigned */}
            {(spray.status === 'created' || spray.status === 'assigned') && isCreatorOrAdmin && (
              <Button variant="outline" size="sm" onClick={() => navigate(`/spray/${spray.id}/edit`)}>
                <Edit className="h-4 w-4" />
                Edit
              </Button>
            )}

            {/* Assign — creator/admin when created */}
            {spray.status === 'created' && isCreatorOrAdmin && (
              <Button variant="primary" size="sm" onClick={() => setShowAssign(true)}>
                <UserCheck className="h-4 w-4" />
                Assign Operator
              </Button>
            )}

            {/* Start — assigned operator only */}
            {/* Start re-targets while a calibration or a pre-start is
                outstanding. The cards below say why and offer the way out. */}
            {spray.status === 'assigned' && isOperator && (
              <Button
                variant="primary"
                size="sm"
                // A blocked Start does not sit there inert: pressing it is the
                // operator saying "I want to begin", and the answer is the
                // screen that unblocks it rather than a disabled button they
                // have to work out for themselves.
                onClick={
                  blockedBy === 'calibration' ? openCalibration
                  : blockedBy === 'pre-start' ? () => setPreStartMachine(preStartOutstanding[0])
                  : () => handleStart()
                }
                disabled={startingSpray}
                title={
                  blockedBy === 'calibration'
                    ? 'This spray requires the sprayer to be calibrated first'
                  : blockedBy === 'pre-start'
                    ? `${preStartOutstanding[0].equipment_name} needs a pre-start check today`
                  : undefined
                }
              >
                {blockedBy === 'calibration' ? <Gauge className="h-4 w-4" />
                  : blockedBy === 'pre-start' ? <ClipboardCheck className="h-4 w-4" />
                  : <Play className="h-4 w-4" />}
                {startingSpray ? 'Starting...'
                  : blockedBy === 'calibration' ? 'Calibrate to start'
                  : blockedBy === 'pre-start' ? 'Pre-start to start'
                  : 'Start Spray'}
              </Button>
            )}

            {/* Sign off — in_progress operator only */}
            {spray.status === 'in_progress' && isOperator && (
              <Button variant="primary" size="sm" onClick={() => setShowSignOff(true)}>
                <CheckCircle className="h-4 w-4" />
                Finish spray
              </Button>
            )}

            {/* Cancel — creator/admin if not completed */}
            {spray.status !== 'completed' && spray.status !== 'cancelled' && isCreatorOrAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCancel(true)}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                <XCircle className="h-4 w-4" />
                Cancel
              </Button>
            )}
          </div>
        }
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {jobSheetError && <p className="text-sm text-red-600">{jobSheetError}</p>}
        {/* The operator mid-spray is here to start the next tank, tank after
            tank — so the tank log leads, not a scroll past the plan every load. */}
        {operatorSpraying && (
          <TankLogPanel
            spray={spray}
            canRecord={isOperator}
            canAmend={isCreatorOrAdmin}
            onChange={loadSpray}
          />
        )}

        {/* Tracking starts with a tank, and this card is where a GPS failure
            surfaces — so it sits beside the tank log rather than far below. */}
        {operatorSpraying && (
          <div className="bg-white border border-neutral-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide mb-3">GPS Tracking</h3>
            <SprayWaypointTracker sprayId={spray.id} isActive={true} />
          </div>
        )}

        {/* Finishing the job gets its own unmistakable control. The GPS card's
            pause and the tank log's "Finish this tank" both sit above this, and
            an operator who pressed either thinking the spray was done left it
            open with no sign-off. */}
        {operatorSpraying && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-2">
            <button
              type="button"
              onClick={() => setShowSignOff(true)}
              className="w-full flex items-center justify-center gap-2 bg-green-700 hover:bg-green-800 text-white text-base font-semibold px-4 py-3 rounded-lg transition-colors"
            >
              <CheckCircle className="h-5 w-5" />
              Finish this spray
            </button>
            <p className="text-xs text-neutral-600 text-center">
              When the last tank is empty. You'll confirm the totals and sign the spray off.
            </p>
          </div>
        )}

        {/* Status timeline */}
        <div className="bg-white border border-neutral-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide mb-4">Progress</h3>
          <StatusTimeline status={spray.status} />
          {spray.cancellation_reason && (
            <p className="text-sm text-red-600 mt-3">
              <strong>Cancellation reason:</strong> {spray.cancellation_reason}
            </p>
          )}
        </div>

        {/* Two-column layout for main info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Spray details */}
          <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-3">
            <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">Details</h3>

            <DetailRow icon={<Calendar className="h-4 w-4 text-neutral-400" />} label="Scheduled">
              {formatLocalDateInTz(spray.scheduled_date, timezone)}
              {spray.scheduled_start_time && ` at ${spray.scheduled_start_time}`}
            </DetailRow>

            <DetailRow icon={<User className="h-4 w-4 text-neutral-400" />} label="Created by">
              {spray.creator_name || '—'}
            </DetailRow>

            <DetailRow icon={<UserCheck className="h-4 w-4 text-neutral-400" />} label="Operator">
              {spray.operator_name || <span className="text-neutral-400 italic">Not assigned</span>}
            </DetailRow>

            {(spray.equipment_name || spray.prime_mover_equipment_name) && (
              <DetailRow icon={<Package className="h-4 w-4 text-neutral-400" />} label="Equipment">
                {spray.equipment_name && <>Sprayer: <span className="font-medium">{spray.equipment_name}</span></>}
                {spray.equipment_name && spray.prime_mover_equipment_name && <span className="text-neutral-300"> · </span>}
                {spray.prime_mover_equipment_name && <>Tractor: <span className="font-medium">{spray.prime_mover_equipment_name}</span></>}
                {spray.nozzle_type && <span className="text-neutral-400"> · {spray.nozzle_type}</span>}
              </DetailRow>
            )}
            {!spray.equipment_name && !spray.prime_mover_equipment_name && spray.equipment_used && (
              <DetailRow icon={<Package className="h-4 w-4 text-neutral-400" />} label="Equipment">
                {spray.equipment_used}
                {spray.nozzle_type && ` · ${spray.nozzle_type}`}
              </DetailRow>
            )}

            {spray.is_nursery ? (
              <div className="pt-2 border-t border-neutral-100 space-y-2">
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-neutral-500">How it goes on</span>
                  <span className="font-medium text-right">
                    {spray.application_method ? APPLICATION_METHOD_LABELS[spray.application_method] : '—'}
                  </span>
                </div>
                {spray.runoff_l_per_plant != null && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-neutral-500">Run-off</span>
                    <span className="font-medium">{Number(spray.runoff_l_per_plant)} L per plant</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-neutral-500">Plants</span>
                  <span className="font-medium">{(spray.target_plant_count ?? 0).toLocaleString()}</span>
                </div>
                <p className="text-xs text-neutral-500">
                  Mixed per 100 L and sprayed to run-off. The hectares, water and L/ha come from the
                  tank log and the GPS, tank by tank.
                </p>
              </div>
            ) : (
            <div className="pt-2 border-t border-neutral-100 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-neutral-500">Row pattern</span>
                <span className="font-medium">{spray.row_spacing_pattern.replace(/_/g, ' ')}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-neutral-500">Coverage</span>
                <span className="font-medium">{spray.spray_coverage_pct}% of target area</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-neutral-500">Concentration</span>
                <span className="font-medium">{spray.concentration_factor}× label rate</span>
              </div>
              {spray.total_target_area_ha != null && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-neutral-500">Total area</span>
                  <span className="font-medium">{Number(spray.total_target_area_ha).toFixed(2)} ha</span>
                </div>
              )}
              {spray.effective_area_ha != null && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-neutral-500">Effective area</span>
                  <span className="font-medium text-primary-700">{Number(spray.effective_area_ha).toFixed(2)} ha</span>
                </div>
              )}
            </div>
            )}
          </div>

          {/* Chemical & inventory */}
          <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-3">
            <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">
              {isMix ? `Tank mix (${spray.chemicals!.length} products)` : 'Chemical'}
            </h3>

            {/* A mix lists every product the same way — name, target, rate on
                one row — primary first. Each product is dosed independently, so
                the primary's rate must sit beside the primary, not in a summary
                row below its partners where it reads as the last partner's. */}
            {isMix ? (
              <div className="space-y-2.5">
                {[...spray.chemicals!]
                  .sort((a, b) => Number(!!b.is_primary) - Number(!!a.is_primary))
                  .map(c => {
                    const name = c.product_name || (c.is_primary ? spray.chemical_name : null);
                    const target = c.target_pest ?? (c.is_primary ? spray.target_pest : null);
                    const rate = c.rate ?? (c.is_primary ? spray.rate_per_ha : null);
                    const factor = c.concentration_factor ?? (c.is_primary ? spray.concentration_factor : null);
                    const unit = c.rate_unit ?? (c.is_primary ? spray.rate_unit : null);
                    return (
                      <div key={c.id} className="flex items-start justify-between gap-2 text-sm">
                        <div className="min-w-0">
                          <span className="inline-flex items-center gap-1 font-semibold text-neutral-800">
                            <FlaskConical className="h-3.5 w-3.5 text-primary-500 shrink-0" />
                            {name}
                          </span>
                          {c.is_primary && spray.chemical_trade_name && (
                            <span className="block text-xs text-neutral-500">{spray.chemical_trade_name}</span>
                          )}
                          {c.product_kind === 'fertiliser' && (
                            <span className="block text-xs text-green-800">Foliar fertiliser</span>
                          )}
                          {target && (
                            <span className="block text-xs text-neutral-400">{target}</span>
                          )}
                        </div>
                        {rate != null && (
                          <span className="font-medium text-neutral-700 shrink-0">
                            {toNumOr(rate) * toNumOr(factor, 1)} {unit}
                          </span>
                        )}
                      </div>
                    );
                  })}
              </div>
            ) : (
              <>
                <p className="font-semibold text-neutral-800">{spray.chemical_name}</p>
                {spray.chemical_trade_name && (
                  <p className="text-sm text-neutral-500">{spray.chemical_trade_name}</p>
                )}
              </>
            )}

            {spray.signal_word && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold uppercase ${
              spray.signal_word === 'DANGER'  ? 'bg-red-100 text-red-700' :
              spray.signal_word === 'WARNING' ? 'bg-orange-100 text-orange-700' :
              'bg-yellow-100 text-yellow-700'
            }`}>
                {spray.signal_word}
              </span>
            )}

            {spray.ppe_required && spray.ppe_required.length > 0 && (
              <div>
                <p className="text-xs text-neutral-500 mb-1">PPE required</p>
                <div className="flex flex-wrap gap-1">
                  {spray.ppe_required.map(ppe => (
                    <span key={ppe} className="bg-neutral-100 text-neutral-700 text-xs px-2 py-0.5 rounded">
                      {ppe}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-1.5 pt-2 border-t border-neutral-100">
              {!isMix && spray.rate_per_ha != null && (
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-500">Rate</span>
                  <span className="font-medium">
                    {toNumOr(spray.rate_per_ha) * toNumOr(spray.concentration_factor, 1)} {spray.rate_unit}
                  </span>
                </div>
              )}
              {!isMix && spray.target_pest && (
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-500">Target pest</span>
                  <span className="font-medium">{spray.target_pest}</span>
                </div>
              )}
              {/* Across the whole mix, not just the primary — a tank mix is only
                  as free to harvest as its most restrictive product. */}
              {effectiveWhpDays != null && (
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-500">
                    WHP (domestic){isMix ? ', longest in mix' : ''}
                  </span>
                  <span className="font-medium text-amber-700">{effectiveWhpDays} days</span>
                </div>
              )}
              {effectiveReiHours != null && (
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-500">
                    Re-entry interval{isMix ? ', longest in mix' : ''}
                  </span>
                  <span className="font-medium">{effectiveReiHours} hours</span>
                </div>
              )}
            </div>

            {/* Volume summary */}
            {(spray.total_chemical_used != null || spray.total_volume_mixed != null) && (
              <div className="pt-2 border-t border-neutral-100 space-y-1">
                {spray.total_chemical_used != null && (
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-500">Chemical used</span>
                    <span className="font-medium">
                      {Number(spray.total_chemical_used).toFixed(1)} {(spray.rate_unit || 'L/ha').split('/')[0]}
                    </span>
                  </div>
                )}
                {spray.total_volume_mixed != null && (
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-500">Volume mixed</span>
                    <span className="font-medium">{Number(spray.total_volume_mixed).toFixed(1)} L</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Per tank and for the whole job — the plan, before and during the
            spray. A finished spray's actuals are in the reconciliation. */}
        {spray.status !== 'cancelled' && (
          <TankDoseTable
            title={spray.status === 'completed' ? 'Planned mix — per tank and whole job' : 'What goes in each tank'}
            products={tankDoseProducts}
            tankCapacityL={tankSetup?.capacityL}
            outputRateLpha={tankRateLpha}
            totalWaterL={jobWaterL}
            areaHa={jobAreaHa}
          />
        )}

        {/* Targets */}
        {spray.targets.length > 0 && (
          <div className="bg-white border border-neutral-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide mb-3">
              {spray.is_nursery ? 'Rows and places' : 'Spray Targets'}
            </h3>
            <div className="flex flex-wrap gap-2">
              {spray.targets.map(t => (
                <span
                  key={t.id}
                  className="inline-flex max-w-full items-center gap-1.5 bg-neutral-50 border border-neutral-200 rounded-full px-3 py-1 text-sm"
                >
                  {spray.is_nursery
                    ? <Sprout className="h-3.5 w-3.5 shrink-0 text-green-600" />
                    : <MapPin className="h-3.5 w-3.5 shrink-0 text-green-600" />}
                  <span className="min-w-0 break-words">{t.target_name}</span>
                  {!spray.is_nursery && (
                    <span className="text-neutral-400 text-xs capitalize">({t.target_type.replace('_', ' ')})</span>
                  )}
                  {!spray.is_nursery && t.area_hectares && (
                    <span className="text-neutral-400 text-xs">{Number(t.area_hectares).toFixed(1)} ha</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* The lots a nursery spray landed on: as planned until it is
            completed, then what stood there when it was sprayed. */}
        {/* A quarantine treatment of a load: each customer's order signed off as treated. */}
        {spray.is_nursery && <SprayQuarantineCard sprayId={spray.id} sprayStatus={spray.status} />}

        {spray.is_nursery && <SprayTargetLotsCard lots={spray.target_lots ?? []} status={spray.status} />}

        {/* Held over a withholding-period conflict — the decision, and who may
            make it, live here. Above the plan detail because it governs whether
            any of it can happen. */}
        <WhpOverridePanel spray={spray} onDecided={loadSpray} />

        {/* Nursery stock has no harvest date: said in words, never left to
            read as cleared. */}
        {spray.is_nursery && (
          <div className="bg-neutral-50 border border-neutral-200 rounded-xl px-5 py-4 text-sm space-y-1">
            <p className="text-neutral-800">
              <strong>Withholding period:</strong> not applicable to nursery stock.
            </p>
            {effectiveReiHours != null && (
              <p className="text-neutral-700">
                <strong>Re-entry:</strong> {effectiveReiHours} hours{isMix ? ' (longest in the mix)' : ''}. Keep
                pickers out of these rows until then.
              </p>
            )}
          </div>
        )}

        {/* WHP warning */}
        {!spray.is_nursery && harvestDate && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-800">
                Withholding period: {effectiveWhpDays} days
                {isMix && <span className="font-normal"> (longest in the mix)</span>}
              </p>
              <p className="text-sm text-amber-700">
                Earliest safe harvest: <strong>{formatLocalDateInTz(harvestDate, timezone)}</strong>
              </p>
            </div>
          </div>
        )}

        {/* Coverage map */}
        {(spray.status === 'in_progress' || spray.status === 'completed') && (
          <div className="bg-white border border-neutral-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">Coverage Map</h3>
              {coverage && (
                <span className={`text-sm font-semibold ${
                (coverage.covered_pct || 0) >= 80 ? 'text-green-600' :
                (coverage.covered_pct || 0) >= 50 ? 'text-amber-600' : 'text-red-600'
              }`}>
                  {coverage.covered_pct != null ? `${coverage.covered_pct.toFixed(1)}% covered` : 'Calculating...'}
                </span>
              )}
            </div>
            <SprayCoverageMap
              targets={spray.targets}
              sprayId={spray.id}
              coverage={coverage}
            />
            {coverage && (
              <p className="text-xs text-neutral-400 mt-2">
                {coverage.total_waypoints} waypoints · {coverage.target_area_ha.toFixed(2)} ha target area
              </p>
            )}
          </div>
        )}

        {startError && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-3 flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{startError}</p>
          </div>
        )}

        {/* Calibration. Shown to the operator whenever the spray names a sprayer:
            before a run it is the gate, after it is the record of what the
            numbers on this page were derived from. */}
        {spray.equipment_id && spray.status !== 'cancelled' && (
          <CalibrationStatusCard
            /* Once the spray is finished it shows the calibration it was RUN
               at, frozen at completion — not whatever the machine has been
               measured at since. Recalibrating the sprayer next week must not
               rewrite what this record says it applied, and a completed spray
               with no snapshot says so rather than borrowing a later one. */
            calibration={spray.status === 'completed'
              ? spray.applied_calibration ?? null
              : spray.latest_calibration}
            asApplied={spray.status === 'completed'}
            equipmentName={spray.equipment_name}
            required={spray.calibration_required === true}
            satisfied={!!spray.calibration_id}
            onCalibrate={isOperator && spray.status !== 'completed' ? openCalibration : undefined}
          />
        )}

        {/* Pre-start. Sits directly under the calibration card because the two
            are the same kind of statement — what has to be true of the machines
            before this spray may begin. Both machines on the rig are listed,
            since the tractor is the one easy to forget: the job is named after
            the sprayer, but the brakes, the PTO guard and the hitch are not. */}
        {preStartMachines.length > 0 && spray.status !== 'cancelled' && (
          <PreStartStatusCard
            machines={preStartMachines}
            /* Once the spray has begun it is a record, not a gate — the gate
               only ever applies at start. Keeping it live would also nag a
               night spray that ran past midnight into a day its morning
               check no longer covers, about a machine already in the field. */
            historic={spray.status === 'in_progress' || spray.status === 'paused'
                      || spray.status === 'completed'}
            onRunCheck={isOperator && spray.status === 'assigned' ? setPreStartMachine : undefined}
          />
        )}

        {/* Compatibility — a mix of one has nothing to check against itself */}
        {isMix && spray.status !== 'cancelled' && (
          <MixCompatibilityPanel chemicalIds={mixChemicalIds} />
        )}

        {/* Tank log — the record of what was actually mixed, load by load.
            Mid-spray it is rendered at the top of the page instead. */}
        {!operatorSpraying && (spray.status === 'in_progress' || spray.status === 'paused' || spray.status === 'completed') && (
          <TankLogPanel
            spray={spray}
            canRecord={isOperator}
            canAmend={isCreatorOrAdmin}
            onChange={loadSpray}
          />
        )}

        {/* The frozen verdict, once the spray has been reconciled */}
        {spray.reconciliation && (
          <ReconciliationPanel reconciliation={spray.reconciliation} />
        )}

        {/* Weather limits — what this spray may run in, and how it stands.
            Above the weather panel because it is the judgement; the panel
            below is the readings it was judged on. */}
        {spray.status !== 'cancelled' && (
          <WeatherLimitsCard
            key={weatherLimitsKey}
            sprayId={spray.id}
            status={spray.status}
            canEdit={isCreatorOrAdmin}
          />
        )}

        {/* Weather */}
        <div className="bg-white border border-neutral-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide mb-3">Weather</h3>
          <WeatherPanel forecastFromSpray={spray} liveWeather={liveWeather} status={spray.status} />
        </div>

        {/* Notes */}
        {(spray.pre_spray_notes || spray.post_spray_notes) && (
          <div className="bg-white border border-neutral-200 rounded-xl p-5 space-y-3">
            <h3 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">Notes</h3>
            {spray.pre_spray_notes && (
              <div>
                <p className="text-xs font-medium text-neutral-400 uppercase mb-1">Pre-spray</p>
                <p className="text-sm text-neutral-700">{spray.pre_spray_notes}</p>
              </div>
            )}
            {spray.post_spray_notes && (
              <div>
                <p className="text-xs font-medium text-neutral-400 uppercase mb-1">Post-spray</p>
                <p className="text-sm text-neutral-700">{spray.post_spray_notes}</p>
              </div>
            )}
          </div>
        )}

        {/* Sign-off block */}
        {spray.status === 'completed' && spray.signed_off_by && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <h3 className="text-sm font-semibold text-green-800">Completed</h3>
            </div>
            <div className="text-sm text-green-700 space-y-1">
              <p>Signed off by: <strong>{spray.signed_off_by_name || 'Operator'}</strong></p>
              {spray.completed_at && (
                <p>At: <strong>{formatDateTimeInTz(spray.completed_at, timezone)}</strong></p>
              )}
              {spray.sign_off_location_lat != null && (
                <p>
                  Location: {Number(spray.sign_off_location_lat).toFixed(5)},{' '}
                  {Number(spray.sign_off_location_lng).toFixed(5)}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Modals */}
        {weatherBreach && (
          <WeatherLimitAckDialog
            check={weatherBreach}
            busy={startingSpray}
            onConfirm={() => handleStart(true)}
            onCancel={() => setWeatherBreach(null)}
          />
        )}
        {showAssign && (
          <AssignOperatorModal
            sprayId={spray.id}
            onClose={() => setShowAssign(false)}
            onAssigned={() => { setShowAssign(false); loadSpray(); }}
          />
        )}
        {showCalibration && calibrationEquipment && (
          <CalibrationWizard
            equipment={calibrationEquipment}
            sprayApplicationId={spray.id}
            suggestedRowSpacingM={suggestedRowSpacing}
            onClose={() => setShowCalibration(false)}
            onSaved={() => { setShowCalibration(false); loadSpray(); }}
          />
        )}
        {preStartMachine && (
          <RunChecklistModal
            equipmentId={preStartMachine.equipment_id}
            equipmentName={preStartMachine.equipment_name}
            equipmentType={preStartMachine.equipment_type}
            /* Recorded against this spray for provenance. The gate itself is
               day-based, so the link is a record of why the check was done,
               not what makes it count. */
            sprayApplicationId={spray.id}
            onClose={() => setPreStartMachine(null)}
            onCompleted={() => { setPreStartMachine(null); loadSpray(); }}
          />
        )}

        {showSignOff && (
          <SignOffModal
            spray={spray}
            onClose={() => setShowSignOff(false)}
            onSigned={() => { setShowSignOff(false); loadSpray(); }}
          />
        )}
        {showCancel && (
          <CancelModal
            sprayId={spray.id}
            onClose={() => setShowCancel(false)}
            onCancelled={() => { setShowCancel(false); loadSpray(); }}
          />
        )}
      </div>
    </>
  );
}

// ---- Small helper ----
function DetailRow({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <span className="mt-0.5 flex-shrink-0">{icon}</span>
      <span className="text-neutral-500 min-w-[80px]">{label}</span>
      <span className="font-medium text-neutral-800">{children}</span>
    </div>
  );
}

export default SprayDetail;
